import type {
  MenuItem,
  MenuItemDetail,
  MenuModifierGroup,
  MenuModifierOption,
  SizePrice,
} from '../services/restaurantService';

/**
 * Variant + add-on rules for the customer picker. These mirror the server's strict validation
 * (ModifierServiceImpl.resolve / MenuItemApiHelper.validateLine) so the guest sees the same
 * message before the request is sent. Design doc §6–§8.4, D2–D5.
 */

export interface MenuOrderSelections {
  sizeTypeId?: number;
  modifierOptionIds?: number[];
  /** Cooking request for this line ("" / undefined = none). */
  itemNote?: string;
}

type OptionsItem = Pick<
  MenuItem | MenuItemDetail,
  'sizePrices' | 'variantLabel' | 'defaultSizeTypeId' | 'modifierGroups' | 'hasOptions'
>;

export const DEFAULT_ITEM_NOTE_MAX = 200;
export const DEFAULT_ORDER_NOTE_MAX = 500;

export function getVariantLabel(item: Pick<OptionsItem, 'variantLabel'>): string {
  const label = item.variantLabel?.trim();
  return label || 'Size';
}

export function getVariantName(size: SizePrice | undefined): string {
  if (!size) return '';
  return (
    size.label?.trim() ||
    size.displayName?.trim() ||
    size.sizeTypeName?.trim() ||
    size.sizeTypeCode?.trim() ||
    'Regular'
  );
}

export function getModifierGroups(item: Pick<OptionsItem, 'modifierGroups'>): MenuModifierGroup[] {
  return Array.isArray(item.modifierGroups) ? item.modifierGroups : [];
}

/** Group applies to the chosen variant (no variant list = every variant). */
export function groupAppliesTo(group: MenuModifierGroup, sizeTypeId?: number): boolean {
  const ids = group.sizeTypeIds || [];
  return ids.length === 0 || (sizeTypeId != null && ids.indexOf(sizeTypeId) >= 0);
}

export function groupsForVariant(
  item: Pick<OptionsItem, 'modifierGroups'>,
  sizeTypeId?: number
): MenuModifierGroup[] {
  return getModifierGroups(item).filter((g) => groupAppliesTo(g, sizeTypeId));
}

export function groupMin(group: MenuModifierGroup): number {
  return group.minSelect != null && group.minSelect > 0 ? group.minSelect : 0;
}

/** Infinity when the group has no upper limit (maxSelect <= 0). */
export function groupMax(group: MenuModifierGroup): number {
  return group.maxSelect != null && group.maxSelect > 0 ? group.maxSelect : Infinity;
}

export function isSingleSelectGroup(group: MenuModifierGroup): boolean {
  return groupMax(group) === 1;
}

export function isGroupRequired(group: MenuModifierGroup): boolean {
  return groupMin(group) > 0;
}

export function optionPrice(option: MenuModifierOption, sizeTypeId?: number): number {
  if (sizeTypeId != null && option.priceBySize) {
    const v = option.priceBySize[String(sizeTypeId)];
    if (v != null && !Number.isNaN(Number(v))) {
      return Number(v);
    }
  }
  const base = Number(option.priceDelta ?? 0);
  return Number.isNaN(base) ? 0 : base;
}

export function isOptionAvailable(option: MenuModifierOption, sizeTypeId?: number): boolean {
  if (option.isAvailable === false) return false;
  if (sizeTypeId != null && (option.unavailableSizeTypeIds || []).indexOf(sizeTypeId) >= 0) {
    return false;
  }
  return true;
}

function availableSizes(item: Pick<OptionsItem, 'sizePrices'>): SizePrice[] {
  return (item.sizePrices || []).filter((s) => s.isAvailable !== false);
}

/** Item has something to choose: add-on groups or more than one variant. */
export function itemHasOptions(item: OptionsItem): boolean {
  if (typeof item.hasOptions === 'boolean') {
    return item.hasOptions;
  }
  return availableSizes(item).length > 1 || getModifierGroups(item).length > 0;
}

/**
 * Drops selections that no longer apply (after a variant change): options in groups not offered
 * on the variant, options out of stock on it, and picks beyond a group's maximum.
 */
export function pruneSelections(
  item: Pick<OptionsItem, 'modifierGroups'>,
  selections: MenuOrderSelections
): MenuOrderSelections {
  const picked = selections.modifierOptionIds || [];
  const kept: number[] = [];
  groupsForVariant(item, selections.sizeTypeId).forEach((group) => {
    const max = groupMax(group);
    let count = 0;
    // Earlier picks win when a group is over its limit.
    picked.forEach((id) => {
      const opt = (group.options || []).find((o) => o.id === id);
      if (!opt || count >= max) return;
      if (!isOptionAvailable(opt, selections.sizeTypeId)) return;
      kept.push(opt.id);
      count += 1;
    });
  });
  const ordered = picked.filter((id) => kept.indexOf(id) >= 0);
  return { ...selections, modifierOptionIds: ordered };
}

/**
 * Tapping an option: radio groups (max 1) replace the pick (tap again to clear an optional one);
 * checkbox groups toggle and ignore taps beyond the maximum.
 */
export function toggleOption(
  selections: MenuOrderSelections,
  group: MenuModifierGroup,
  optionId: number
): MenuOrderSelections {
  const current = selections.modifierOptionIds || [];
  const groupIds = (group.options || []).map((o) => o.id);
  const inGroup = current.filter((id) => groupIds.indexOf(id) >= 0);
  const isPicked = current.indexOf(optionId) >= 0;
  if (isSingleSelectGroup(group)) {
    const withoutGroup = current.filter((id) => groupIds.indexOf(id) < 0);
    if (isPicked) {
      return isGroupRequired(group)
        ? selections
        : { ...selections, modifierOptionIds: withoutGroup };
    }
    return { ...selections, modifierOptionIds: [...withoutGroup, optionId] };
  }
  if (isPicked) {
    return { ...selections, modifierOptionIds: current.filter((id) => id !== optionId) };
  }
  if (inGroup.length >= groupMax(group)) {
    return selections;
  }
  return { ...selections, modifierOptionIds: [...current, optionId] };
}

export interface SelectedOption {
  group: MenuModifierGroup;
  option: MenuModifierOption;
  price: number;
}

/** Selected options that apply to the chosen variant, in menu order. */
export function getSelectedOptions(
  item: Pick<OptionsItem, 'modifierGroups'>,
  selections: MenuOrderSelections
): SelectedOption[] {
  const picked = selections.modifierOptionIds || [];
  const out: SelectedOption[] = [];
  groupsForVariant(item, selections.sizeTypeId).forEach((group) => {
    (group.options || []).forEach((option) => {
      if (picked.indexOf(option.id) >= 0) {
        out.push({ group, option, price: optionPrice(option, selections.sizeTypeId) });
      }
    });
  });
  return out;
}

/** Sum of add-on prices per unit (design D3: added to the variant price). */
export function getAddonsAmount(
  item: Pick<OptionsItem, 'modifierGroups'>,
  selections: MenuOrderSelections
): number {
  return getSelectedOptions(item, selections).reduce((sum, s) => sum + s.price, 0);
}

function wholeRupees(amount: number | null | undefined): number {
  const n = Number(amount);
  return Number.isFinite(n) ? Math.round(n) : 0;
}

/** Unit price of a line: the (rounded) variant price plus the add-ons, rounded once as a sum. */
export function lineUnitPrice(variantPrice: number, addonsAmount: number): number {
  return wholeRupees(variantPrice) + wholeRupees(addonsAmount);
}

/** Line total: the rounded unit price times the quantity (never the other way round). */
export function lineTotal(variantPrice: number, addonsAmount: number, quantity: number): number {
  return lineUnitPrice(variantPrice, addonsAmount) * Math.max(0, Math.floor(Number(quantity) || 0));
}

/**
 * Same format as the server's optionsSummary (MenuItemApiHelper.buildOptionsNote +
 * ResolvedModifiers.noteParts): the variant name first when the item has more than one variant,
 * consecutive picks of one CHOICE/PREFERENCE group as "Group: A, B", add-ons as "+ Name",
 * parts joined with " · ".
 */
export function buildOptionsSummary(item: OptionsItem, selections: MenuOrderSelections): string {
  const parts: string[] = [];
  const allSizes = item.sizePrices || [];
  if (allSizes.length > 1 && selections.sizeTypeId != null) {
    const size = allSizes.find((s) => s.sizeTypeId === selections.sizeTypeId);
    const name = getVariantName(size);
    if (name) parts.push(name);
  }
  let lastGroupId: number | null = null;
  getSelectedOptions(item, selections).forEach(({ group, option }) => {
    const name = (option.optionName || option.optionCode || '').trim();
    if (!name) return;
    const type = (group.groupType || 'ADDON').toUpperCase();
    if (type === 'ADDON') {
      parts.push(`+ ${name}`);
      lastGroupId = null;
      return;
    }
    if (lastGroupId != null && lastGroupId === group.id && parts.length > 0) {
      parts[parts.length - 1] = `${parts[parts.length - 1]}, ${name}`;
    } else {
      const groupName = (group.groupName || '').trim();
      parts.push(groupName ? `${groupName}: ${name}` : name);
    }
    lastGroupId = group.id;
  });
  return parts.join(' · ');
}

/** Persisted order-line note ("Medium | + Extra cheese") as shown to guests. */
export function formatOptionsNote(note: string | null | undefined): string {
  if (!note) return '';
  return note
    .split(' | ')
    .map((p) => p.trim())
    .filter(Boolean)
    .join(' · ');
}

// Zero-width / bidi format characters (Unicode Cf) except ZWNJ (U+200C) and ZWJ (U+200D), which
// Indic scripts need. Mirrors LineOptions.normalizeNote on the server.
const FORMAT_CHARS =
  /[­؀-؅؜۝܏᠎​‎‏‪-‮⁠-⁤⁦-⁯﻿￹-￻]/g;
// Whitespace and control characters collapse to one space.
// eslint-disable-next-line no-control-regex
const SPACE_OR_CONTROL = /[\s\u0000-\u001F\u007F-\u009F]+/g;

/** Cleaned note as the server stores it; "" when nothing is left. */
export function normalizeNote(note: string | null | undefined): string {
  if (!note) return '';
  return note.replace(FORMAT_CHARS, '').replace(SPACE_OR_CONTROL, ' ').trim();
}

/** Comparison form of a note (case-insensitive, whitespace-collapsed). */
export function noteKey(note: string | null | undefined): string {
  return normalizeNote(note).toLowerCase();
}

/** Characters as the server counts them (code points). */
export function noteLength(note: string | null | undefined): number {
  return Array.from(normalizeNote(note)).length;
}

/** Appends a preset to the note, comma-separated, unless it is already there or too long. */
export function appendNotePreset(note: string | undefined, preset: string, max: number): string {
  const current = (note || '').trim();
  const p = preset.trim();
  if (!p) return current;
  const parts = noteKey(current)
    .split(',')
    .map((s) => s.trim());
  if (parts.indexOf(noteKey(p)) >= 0) {
    return current;
  }
  const next = current ? `${current.replace(/,\s*$/, '')}, ${p}` : p;
  return Array.from(next).length > max ? current : next;
}

/**
 * Mirrors the server's strict line validation. Returns the first problem as a guest-facing
 * message, or null when the selection can be added.
 */
export function validateSelections(
  item: OptionsItem,
  selections: MenuOrderSelections,
  itemNoteMax: number = DEFAULT_ITEM_NOTE_MAX
): string | null {
  const sizes = availableSizes(item);
  const label = getVariantLabel(item).toLowerCase();
  if (sizes.length > 1 && selections.sizeTypeId == null) {
    return `Please choose a ${label}`;
  }
  if (
    selections.sizeTypeId != null &&
    (item.sizePrices || []).length > 0 &&
    !sizes.some((s) => s.sizeTypeId === selections.sizeTypeId)
  ) {
    return `This ${label} is not available right now`;
  }
  const picked = selections.modifierOptionIds || [];
  const applicable = groupsForVariant(item, selections.sizeTypeId);
  for (let i = 0; i < picked.length; i += 1) {
    const id = picked[i];
    const group = applicable.find((g) => (g.options || []).some((o) => o.id === id));
    const option = group ? (group.options || []).find((o) => o.id === id) : undefined;
    if (!option) {
      return 'One of the selected options is not available for this item';
    }
    if (!isOptionAvailable(option, selections.sizeTypeId)) {
      return `${option.optionName || 'An option'} is not available right now`;
    }
  }
  for (let i = 0; i < applicable.length; i += 1) {
    const group = applicable[i];
    const ids = (group.options || []).map((o) => o.id);
    const count = picked.filter((id) => ids.indexOf(id) >= 0).length;
    const name = group.groupName || group.groupCode || 'options';
    if (count > groupMax(group)) {
      return `Choose up to ${group.maxSelect} in ${name}`;
    }
    const min = groupMin(group);
    // The server skips a required group whose options are all out of stock outlet-wide.
    if (min > 0 && ids.length > 0 && count < min) {
      return min > 1 ? `Please choose ${min} in ${name}` : `Please choose ${name}`;
    }
  }
  if (noteLength(selections.itemNote) > itemNoteMax) {
    return `Note is too long (max ${itemNoteMax} characters)`;
  }
  return null;
}
